import React, { MutableRefObject } from 'react';
import {
  Button,
  Column,
  Copy,
  Flex,
  Heading,
  Row,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { Input } from 'src/components/Input/Input';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';

export type GroupPeopleSearchToolbarProps = {
  isLoading: boolean;
  onAddPeopleClick: () => void;
  onToggleAllClick: () => void;
  isToggleAllChecked: boolean;
  currentResults: number;
  totalResults: number;
  searchInputRef: MutableRefObject<HTMLInputElement>;
  onSearchGroupInput: () => void;
  hideAddButton: boolean;
};

export const GroupPeopleSearchToolbar: React.FC<GroupPeopleSearchToolbarProps> =
  ({
    isLoading,
    onAddPeopleClick,
    isToggleAllChecked,
    onToggleAllClick,
    totalResults,
    currentResults,
    searchInputRef,
    onSearchGroupInput,
    hideAddButton,
  }) => {
    const { isPhone, isDesktop } = useScreenSizes();

    const peopleText = isDesktop ? ' people' : '';
    const numberOfPeopleText = `${currentResults} of ${totalResults}${peopleText}`;

    const { sendAnalyticsBefore } = useSendAnalytics({
      eventcategory: 'self-service management tool_people',
      eventaction: 'add people to group_impression',
    });

    return (
      <>
        <Stack y="sm">
          <Heading variant="h4">People</Heading>
        </Stack>
        <Stack y="md">
          <Row
            justifyContent="space-between"
            flexDirection={isPhone ? 'column-reverse' : 'row'}
          >
            <Column desktop={3} tablet={5}>
              <Input
                ref={searchInputRef}
                key="searchTermGroup"
                ariaLabel="Search by name or email"
                placeholder="Search by name or email"
                name="searchTermGroup"
                id="searchTermGroup"
                data-testid="searchTermGroup"
                onInput={onSearchGroupInput}
                prefixIcon={
                  <Copy variant="body" color="secondaryText">
                    <SvgMagnifyingGlass size="16px" />
                  </Copy>
                }
              />
            </Column>
            {!hideAddButton && (
              <Column desktop={2} tablet={3}>
                <Stack y={isPhone ? 'md' : 'none'}>
                  <Button
                    variant="primary"
                    buttonSize="medium"
                    block
                    disabled={isLoading}
                    onClick={() => sendAnalyticsBefore(onAddPeopleClick)}
                    isLoading={isLoading}
                  >
                    Add people
                  </Button>
                </Stack>
              </Column>
            )}
          </Row>
        </Stack>

        <Flex alignItems="center">
          <Stack x="x-sm">
            <Checkbox
              id="select-all"
              name="select-all"
              ariaLabel="select-all"
              cancelLabelBottomMargin
              checked={isToggleAllChecked}
              onChange={() => onToggleAllClick()}
              isMultipleSelection
            />
          </Stack>
          <Stack flex={1}>
            <Row alignItems="center">
              <Column desktop={10} tablet={10} phone={8}>
                <Row>
                  <Column desktop={4}>
                    <Copy variant="body" color="secondaryText">
                      Name
                    </Copy>
                  </Column>
                  {isDesktop && (
                    <Column desktop={8}>
                      <Copy variant="body" color="secondaryText">
                        Email
                      </Copy>
                    </Column>
                  )}
                </Row>
              </Column>
              <Column desktop={2} tablet={2} phone={4}>
                <Row justifyContent="flex-end">
                  <Copy variant="body" color="secondaryText">
                    {numberOfPeopleText}
                  </Copy>
                </Row>
              </Column>
            </Row>
          </Stack>
        </Flex>
      </>
    );
  };
